import { useSessionStore, type SessionUser } from '@/stores/session';
import { visibleDashboardCards } from '@/features/dashboard/action-cards';
import { firstNavSection } from '@/components/layout/nav-groups';

export interface HomeTarget {
  // `true` => Inicio se ofrece y `/` abre el dashboard.
  hasDashboard: boolean;
  // A dónde lleva "ir al inicio": `/`, o la primera sección del menú.
  path: string;
  // Nombre de ese destino, para textos como el `aria-label` del logo.
  label: string;
}

const DASHBOARD_HOME: HomeTarget = { hasDashboard: true, path: '/', label: 'Inicio' };

// Regla de Inicio: si el dashboard no tiene ninguna tarjeta de acción para el
// user, Inicio se oculta y `/` lo lleva directo a su primera sección del menú.
// Un dashboard sin tarjetas es un saludo y, como mucho, un gráfico: nada que
// hacer ahí (caso que la originó: el rol del Estudio de fotos).
//
// Sin ninguna sección tampoco hay a dónde mandarlo: se queda el dashboard, así
// `/` nunca redirige a ninguna parte ni a sí mismo. Sesión sin `permissions`
// (fallback retrocompat): todo abre, hay tarjetas y nada cambia.
//
// Los integradores (`/integrators/:slug`) no cuentan como sección: salen de una
// consulta al back y `/` tendría que esperarla para decidir.
export function resolveHomeTarget(user: Pick<SessionUser, 'permissions'> | null): HomeTarget {
  if (visibleDashboardCards(user).length > 0) return DASHBOARD_HOME;
  const section = firstNavSection(user !== null, user?.permissions);
  if (!section) return DASHBOARD_HOME;
  return { hasDashboard: false, path: section.to, label: section.label };
}

// Lo leen el sidebar (Inicio), las migas (crumb raíz), el logo del celular y el
// guard de `/` (`RequireDashboard`): los cuatro deciden con la misma regla.
export function useHomeTarget(): HomeTarget {
  const user = useSessionStore((s) => s.user);
  return resolveHomeTarget(user);
}
